import './main.less';
import PropTypes from 'prop-types';
import React from 'react';
import { Link } from 'react-router';
import moment from 'moment';
import cn from 'classname';
import qs from 'qs';
import noop from 'lodash/noop';
import find from 'lodash/find';
import capitalize from 'lodash/capitalize';

import Pagination from '../../../shared/pagination';
import { enum as affiliateStatuses } from '../../../../../../../enums/affiliate-statuses';
import { enum as affiliateLevels } from '../../../../../../../enums/affiliate-levels';
import { enum as affiliateRanks } from '../../../../../../../enums/affiliate-ranks';
import { formatMoney } from '../../../shared/format';
import getLangSign from '../../utils/get-lang-sign';

const isEverad = process.env.CPA_ENV === 'everad';

const Table = props =>
	<div className="table-container">
		<table
			className={cn({
				'simple-table affiliates-table': true,
				narrow: props.narrow
			})}>
			<thead className="simple-table__head font-roboto-medium">
				<tr>
					{props.table.headers
						.filter(header =>
							!header.index || !isEverad || !header.index.startsWith('balance_')
							|| props.availableCurrencies.includes(header.index.replace('balance_', ''))
						)
						.map((header, i) =>
							<th
								key={i}
								className={cn({
									'sort-header': header.direction,
									active: props.table.sortedColumn.index === header.index,
									[header.className]: header.className,
									[props.table.sortedColumn.direction]:
										props.table.sortedColumn.index === header.index && props.table.sortedColumn.direction
								})}
								onClick={() => (header.direction ? props.sortByColumn(header.index) : noop)}
							>
								{props.intl(header.title)}
							</th>
						)}
				</tr>
			</thead>

			<tbody className={cn({
				'simple-table__body': true,
				'table-indexing': props.isIndexing,
				'default-table-indexing': props.isIndexing
			})}>
				{props.table.rows.map((row, i) =>
					<Row
						row={row}
						key={i}
						idx={i}
						{...props}
					/>
				)}
			</tbody>
		</table>
		<div className="affiliates-total">{props.intl('Total')}: {props.pagination.total}</div>
		<Pagination pagination={props.pagination} gotoPage={props.gotoPage} />
	</div>;

const affiliateFilterQuery = affiliate => {
	const query = { filters: { affiliates: [affiliate.id] }};

	const entity = {
		filters: [{
			name: 'affiliates',
			values: [{ value: affiliate.id, label: affiliate.email }]
		}]
	};

	return qs.stringify({query, entity});
};

function getRankNameById(rankId) {
	const rank = find(affiliateRanks, { id: rankId });
	return rank ? rank.name : '-';
}

class Row extends React.Component {
	constructor(props) {
		super(props);
		this.state = {
			activeHover: false
		};
	}
	render() {
		const { row, idx, can, loginWithAffiliate, availableCurrencies} = this.props;
		const canSeeProfile = can('view');

		const isBanned = isEverad
			? row.status === affiliateStatuses.banned
			: row.level === affiliateLevels.banned;

		if (row.feature_flags.has_activity_in_rocket) {
			row.has_activity_in_rocket = true;
		}

		return (
			<tr
				className={cn({
					'clickable': this.state.activeHover,
					banned: isBanned,
					'has-individual-terms': row.has_individual_terms,
					'modify-background': idx % 2
				})}
			>
				<td className="id">
					<Link to={canSeeProfile ? `affiliates/profile/${row.id}` : null} className="profile-link">
						<span className={cn(row.is_online && 'online')}>
							{row.id}
						</span>
						{ row.is_suspicious &&  <span className="icon-suspicious" /> }
						{ row.is_possible_fraud &&  <span className="icon-fraud" /> }
						{!!row.available_offers_count && (
							<span
								className='icon-available-offers-count'
								title={`available offers count: ${row.available_offers_count}`}
							/>
						)}
					</Link>
				</td>
				<td>
					<div className='profile-icons'>
						{row.has_activity_in_rocket !== null && (
							<span className={cn('profile-logo-icon-rocket', {
								'profile-logo-icon-rocket-inactive': row.has_activity_in_rocket === false,
								'profile-logo-icon-rocket-active': row.has_activity_in_rocket === true
							})} />
						)}
						{row.has_activity_in_everad !== null && (
							<span className={cn('profile-logo-icon-everad', {
								'profile-logo-icon-everad-inactive': row.has_activity_in_everad === false,
								'profile-logo-icon-everad-active': row.has_activity_in_everad === true
							})} />
						)}
					</div>
				</td>
				<td className="nickname">
					<Link to={canSeeProfile ? `affiliates/profile/${row.id}` : null} className="profile-link">
						<span>
							{row.nickname ? row.nickname : '-'}
						</span>
					</Link>
				</td>
				<td className="contacts"
					onMouseEnter={() => this.setState({activeHover: true})}
					onMouseLeave={() => this.setState({activeHover: false})}
				>
					<a className="profile-link">
						<ul>
							{row.email && <li className="email">{row.email}</li>}
							{row.telegram && <li className="telegram">{row.telegram}</li>}
							{row.skype && <li className="skype">{row.skype}</li>}
							{row.phone && <li className="phone">{row.phone}</li>}
						</ul>
					</a>
				</td>
				<td className="language text-center">
					<Link to={canSeeProfile ? `affiliates/profile/${row.id}` : null}>
						<span>
							{ row.description ? '✔' : '-' }
						</span>
					</Link>
				</td>
				<td className="language">
					<Link to={canSeeProfile ? `affiliates/profile/${row.id}` : null}>
						<span>
							{ getLangSign(row.language) }
						</span>
					</Link>
				</td>
				<td
					className="modify-border-dashed manager"
					onMouseEnter={() => this.setState({activeHover: true})}
					onMouseLeave={() => this.setState({activeHover: false})}
				>
					<a className="manager-link">
						{row.email && <p className="affiliate-manager">{row.manager_name || '-'}</p>}
						{
							row.email && !!row.affiliate_additional_managers
							&& !!row.affiliate_additional_managers.length
							&& row.affiliate_additional_managers
								.filter(item => row.manager_name !== item)
								.map((item, idx) =>
									<p className="affiliate-manager" key={idx}>{item}</p>
								)
						}
					</a>
				</td>
				{isEverad && (
					<td>
						<Link to={canSeeProfile ? `affiliates/profile/${row.id}` : null} className="profile-link">
							<span>
								{capitalize(row.internal_rank)}
							</span>
						</Link>
					</td>
				)}
				{isEverad && (
					<td className="modify-border">
						<Link to={canSeeProfile ? `affiliates/profile/${row.id}` : null} className="profile-link">
							<span
								className={cn({
									'internal-status': true,
									[`${row.internal_status}`]: true
								})}
							>
								{capitalize(row.internal_status)}
							</span>
						</Link>
					</td>
				)}
				{!isEverad && (
					<td className="modify-border">
						<Link to={canSeeProfile ? `affiliates/profile/${row.id}` : null} className="profile-link">
							<span>
								{getRankNameById(row.rank_id)}
							</span>
						</Link>
					</td>
				)}
				{!isEverad && (
					<td className="balance">
						<Link to={canSeeProfile ? `affiliates/profile/${row.id}` : null} className="profile-link">
							<span>
								{row.balance_points}
							</span>
						</Link>
					</td>
				)}
				{availableCurrencies.filter(curr => isEverad || curr === 'usd').map((currency, index) =>
					<td className="modify-border balance" key={index}>
						<Link to={canSeeProfile ? `affiliates/profile/${row.id}` : null} className="profile-link">
							<span>
								{formatMoney(row[`balance_${currency}`], true)}
							</span>
						</Link>
					</td>
				)}
				{isEverad && (
					<td className="referer-domain">
						<Link to={canSeeProfile ? `affiliates/profile/${row.id}` : null} className="profile-link">
							<span title={row.referer || '-'}>
								{row.referer_domain || '-'}
							</span>
						</Link>
					</td>
				)}
				{isEverad && (
					<td className="tags">
						<Link to={canSeeProfile ? `affiliates/profile/${row.id}` : null} className="profile-link">
							<span title={row.tags || '-'}>
								{row.tags && row.tags.length ? row.tags.join(', ') : '-'}
							</span>
						</Link>
					</td>
				)}
				{isEverad && (
					<td className="tags">
						<Link to={canSeeProfile ? `affiliates/profile/${row.id}` : null} className="profile-link">
							<span title={row.lead_sources || '-'}>
								{row.lead_source_names && row.lead_source_names.length ? row.lead_source_names.join(', ') : '-'}
							</span>
						</Link>
					</td>
				)}
				{ !isEverad &&
					<td>
						<Link to={canSeeProfile ? `affiliates/profile/${row.id}` : null} className="profile-link">
							<span>
								{moment(row.created_at).format('DD.MM.YYYY')}
							</span>
						</Link>
					</td>
				}
				<td className="actions">
					{can('view_statistics') && canSeeProfile &&
					<Link to={`statistics?${affiliateFilterQuery(row)}`} target="_blank" className="action_statistics" />
					}
					{
						canSeeProfile &&
						<Link to={canSeeProfile ? `affiliates/profile/${row.id}` : null} className="action_profile" />
					}
					{
						can('edit') &&
						<Link to={canSeeProfile ? `affiliates/edit/${row.id}` : null} className="action_edit" />
					}
					{can('login') &&
					<a
						href="#"
						onClick={e => {
							e.preventDefault();
							loginWithAffiliate(row.id);
						}}
						className="action_login"
					/>}
				</td>
			</tr>
		);}
}

Row.propTypes = {
	row: PropTypes.object,
	idx:PropTypes.number,
	intl: PropTypes.func,
	sortByColumn: PropTypes.func,
	showMoreRows: PropTypes.func,
	remove: PropTypes.func,
	can: PropTypes.func,
	gotoPage: PropTypes.func,
	loginWithAffiliate: PropTypes.func,
	pagination: PropTypes.object,
	narrow: PropTypes.bool,
	isIndexing: PropTypes.bool
};
Table.propTypes = {
	table: PropTypes.object,
	intl: PropTypes.func,
	sortByColumn: PropTypes.func,
	showMoreRows: PropTypes.func,
	remove: PropTypes.func,
	can: PropTypes.func,
	gotoPage: PropTypes.func,
	loginWithAffiliate: PropTypes.func,
	pagination: PropTypes.object,
	narrow: PropTypes.bool,
	isIndexing: PropTypes.bool
};

export default Table;



// WEBPACK FOOTER //
// ./frontend/admin-dashboard/src/modules/affiliates/components/table/main.js